/* eslint-disable @typescript-eslint/no-explicit-any */
import type { IntlShape } from 'react-intl';
import moment from 'moment-timezone';

/** Chuỗi datetime từ DB/API (UTC) → hiển thị giờ VN + nhãn múi giờ (theo locale). */
function formatConflictUtcDbToVn(
  value: unknown,
  formatMessage: IntlShape['formatMessage'],
): string {
  const dash = formatMessage({
    id: 'schedule_conflict.em_dash',
    defaultMessage: '—',
  });
  if (value == null || value === '') return dash;
  let s = String(value).trim().replace(/\.\d+/, '');
  let m = moment.utc(s, moment.ISO_8601, true);
  if (!m.isValid()) {
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(s)) s += ':00';
    m = moment.utc(s, 'YYYY-MM-DD HH:mm:ss', true);
  }
  if (!m.isValid()) return String(value);
  const clock = m.tz('Asia/Ho_Chi_Minh').format('DD/MM/YYYY HH:mm');
  const tag = formatMessage({
    id: 'schedule_conflict.vn_time_tag',
    defaultMessage: '(Vietnam UTC+7)',
  });
  return `${clock} ${tag}`;
}

export type ScheduleConflictApi = {
  source?: string;
  source_id?: number;
  course_id?: number;
  course_title?: string | null;
  cared_by_user_id?: number | null;
  cared_by_display_name?: string | null;
  time_from?: string;
  time_to?: string;
};

function extractErrorObjects(data: any): any[] {
  const errors = data?.errors;
  if (!errors?.length) return [];
  const first = errors[0];
  if (Array.isArray(first)) {
    return first.filter(Boolean);
  }
  return errors.filter(Boolean);
}

export function scheduleConflictLinesFromApiData(
  data: any,
  formatMessage: IntlShape['formatMessage'],
): string[] {
  const rows = extractErrorObjects(data);
  const lines: string[] = [];
  for (const row of rows) {
    const conflicts = row?.schedule_conflicts as
      | ScheduleConflictApi[]
      | undefined;
    if (Array.isArray(conflicts) && conflicts.length) {
      conflicts.forEach(c => {
        const em = formatMessage({
          id: 'schedule_conflict.em_dash',
          defaultMessage: '—',
        });
        const cs = c.cared_by_display_name
          ? c.cared_by_display_name
          : c.cared_by_user_id != null
          ? formatMessage(
              {
                id: 'schedule_conflict.cs_user_ref',
                defaultMessage: 'CS user {ref}',
              },
              { ref: `#${c.cared_by_user_id}` },
            )
          : em;
        const courseInfo = c.course_title
          ? `#${c.course_id} — ${c.course_title}`
          : `#${c.course_id} — ${formatMessage({
              id: 'schedule_conflict.course_no_title',
              defaultMessage: '(No title)',
            })}`;
        const sourceLabel =
          c.source === 'course_session'
            ? formatMessage({
                id: 'schedule_conflict.source_session',
                defaultMessage: 'Trial / test session',
              })
            : formatMessage({
                id: 'schedule_conflict.source_unit',
                defaultMessage: 'Unit (in course)',
              });
        lines.push(
          formatMessage(
            {
              id: 'schedule_conflict.detail_line',
              defaultMessage:
                'Course: {courseInfo} | CS in charge: {csName} | Time: {timeFrom} → {timeTo} | {source}',
            },
            {
              courseInfo,
              csName: cs,
              timeFrom: formatConflictUtcDbToVn(c.time_from, formatMessage),
              timeTo: formatConflictUtcDbToVn(c.time_to, formatMessage),
              source: sourceLabel,
            },
          ),
        );
      });
    } else if (row?.message) {
      lines.push(String(row.message));
    }
  }
  return lines;
}

/** Dùng cho axios error từ api-v2 (notification, toast). */
export function getScheduleConflictOrMessage(
  err: { response?: { data?: any } },
  formatMessage: IntlShape['formatMessage'],
): { lines: string[]; fallback?: string } {
  const lines = scheduleConflictLinesFromApiData(
    err?.response?.data,
    formatMessage,
  );
  if (lines.length) return { lines };
  const rows = extractErrorObjects(err?.response?.data);
  const msg = rows[0]?.message;
  return {
    lines: [],
    fallback: msg != null ? String(msg) : undefined,
  };
}
